import { vec3, quat } from 'gl-matrix';
import { PhysicsBody } from './PhysicsBody';
import { RenderObject } from '../object/RenderObject';
import { computeFromBounds } from './InertiaTensor';
import { BoneHitResult } from './BoneCollisionBody';

export interface ProjectileOptions {
  position: vec3;
  velocity: vec3;
  gravity?: number;
  restitution?: number;
  surfaceOffset?: number;
  maxLifetime?: number;
  maxBounces?: number;
  onHit?: (position: vec3, normal: vec3) => void;
  onBoneHit?: (result: BoneHitResult) => void;
  renderObject?: RenderObject;
  mass?: number;
  initialAngularVelocity?: vec3;
  enableRotation?: boolean;
  persist?: boolean;
  angularDamping?: number;
  alignToVelocity?: boolean;
  stickOnImpact?: boolean;
  penetrationFraction?: number;
}

export class Projectile {
  public body: PhysicsBody;
  public renderObject?: RenderObject;
  public alive: boolean;
  public lifetime: number;
  public maxLifetime: number;
  public bounceCount: number;
  public maxBounces: number;
  public onHit?: (position: vec3, normal: vec3) => void;
  public onBoneHit?: (result: BoneHitResult) => void;
  public persist: boolean;
  public settled: boolean;
  public settleStart: quat | null;
  public settleTarget: quat | null;
  public settleNormal: vec3 | null;
  public settleProgress: number;
  public alignToVelocity: boolean;
  public stickOnImpact: boolean;
  public penetrationFraction: number;
  public forwardRollOffset: number;
  public boneHitCooldown: number;

  constructor(options: ProjectileOptions) {
    let mass = options.mass ?? 1;
    let rotationOptions: {
      mass?: number;
      inverseInertiaDiag?: vec3;
      centerOfMassOffset?: vec3;
      initialAngularVelocity?: vec3;
      angularDamping?: number;
    } = {};

    if (options.enableRotation) {
      rotationOptions.mass = mass;
      rotationOptions.initialAngularVelocity = options.initialAngularVelocity;
      rotationOptions.angularDamping = options.angularDamping;

      if (options.renderObject?.bounds) {
        let scale = options.renderObject.trs.scale;
        let computed = computeFromBounds(mass, options.renderObject.bounds, scale);
        rotationOptions.inverseInertiaDiag = computed.inverseInertiaDiag;
        rotationOptions.centerOfMassOffset = computed.centerOfMassOffset;
        (rotationOptions as any).localMin = computed.localMin;
        (rotationOptions as any).localMax = computed.localMax;
      }
    }

    this.body = new PhysicsBody(vec3.clone(options.position), {
      gravity: options.gravity,
      mode: 'dynamic',
      restitution: options.restitution ?? 0.6,
      surfaceOffset: options.surfaceOffset ?? 0,
      ...rotationOptions,
    });
    vec3.copy(this.body.velocity, options.velocity);

    this.renderObject = options.renderObject;
    this.alive = true;
    this.lifetime = 0;
    this.maxLifetime = options.maxLifetime ?? 10;
    this.bounceCount = 0;
    this.maxBounces = options.maxBounces ?? 5;
    this.onHit = options.onHit;
    this.onBoneHit = options.onBoneHit;
    this.persist = options.persist ?? false;
    this.settled = false;
    this.settleStart = null;
    this.settleTarget = null;
    this.settleNormal = null;
    this.settleProgress = 0;
    this.alignToVelocity = options.alignToVelocity ?? false;
    this.stickOnImpact = options.stickOnImpact ?? false;
    this.penetrationFraction = options.penetrationFraction ?? 0.2;
    this.boneHitCooldown = 0;

    // Determine forward axis from bounds: the longest dimension is the shaft.
    // TRS applies Rx * Ry * Rz to points (Rz first, then Ry, then Rx).
    // For +Z forward: no roll needed, yaw(Ry) aims in XZ, pitch(Rx) tilts up/down.
    // For +Y forward: Rz(-90°) maps +Y→+X, then Ry aims +X in XZ, then Rx tilts.
    this.forwardRollOffset = 0;
    if (this.alignToVelocity && options.renderObject?.bounds) {
      let b = options.renderObject.bounds;
      let ex = b.localMax[0] - b.localMin[0];
      let ey = b.localMax[1] - b.localMin[1];
      let ez = b.localMax[2] - b.localMin[2];
      if (ey >= ex && ey >= ez) {
        this.forwardRollOffset = -90; // shaft along +Y
      } else if (ex >= ey && ex >= ez) {
        this.forwardRollOffset = 90; // shaft along +X
      }
      // +Z forward: roll stays 0
    }
  }
}
